import React, { useEffect, useState } from "react";
import TextInput from "./../../../../../../../common/InputField/TextInput";
import moment from "moment";
import CustomTable from "./../../../../../../../common/CustomTable";
import Skeleton from "react-loading-skeleton";
import Dropdown from "./../../../../../../../common/Dropdown";
import { truncate } from "lodash";
import ReactECharts from "echarts-for-react";
import useGetAllWarehouses from "hooks/useGetAllWarehouses";
import useGetSalesForecast from "hooks/inventoryHooks/useGetSalesForecast";
import millify from "millify";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import SynthTooltip from "components/common/SynthTooltip";
import lastSyncedTimeDiff from "helpers/lastSyncedTimeDiff";
import MultiChannelSelect from "components/common/MultiChannelSelect";
import { useParams } from "react-router-dom";

const SalesComponent = ({ uom, lastSyncedDate, inventoryId, type }: { uom: string; lastSyncedDate: string, inventoryId?: string, type?: string }) => {

    const { productId: productIdInUrl } = useParams();
    const productId = type === "chat" ? inventoryId : productIdInUrl;
    const [selectedPeriod, setSelectedPeriod] = useState<string>("6 months");
    const [displayPeriod, setDisplayPeriod] = useState<string>("");

    const {
        warehouseValue: warehouseDataValue,
        searchTwo,
        handleDebouncedChangeTwo,
        handleSearchTwo,
        searchResult,
        handleFilterChange: filterChange,
        handleClearFilter: clearFilter,
    } = useGetAllWarehouses();

    const {
        salesForecast,
        fetchingSalesForecast,
        setDates,
        setWarehouseId,
        handleChannelChange: handleSalesForecastChannelChange,
        channel: salesForecastChannelFilterValue,
    } = useGetSalesForecast(productId);

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const salesChannelOption = (salesChannel as string[])
        ?.filter(
            (channel) =>
                channel?.toLowerCase() === "shopify" ||
                channel?.toLowerCase() === "retail" ||
                channel?.toLowerCase() === "amazon"
        )
        ?.map((channel) => {
            return {
                label: channel,
                value: channel,
                icon:
                    channel?.toLowerCase() === "shopify" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/shopify_w87tn3.svg"
                            alt="shopify"
                            className="w-4 h-4"
                        />
                    ) : channel?.toLowerCase() === "amazon" ? (
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/amazon_1_aeftdf.svg"
                            alt="amazon"
                            className="w-4 h-4"
                        />
                    ) : (
                        <i className="ri-store-2-fill text-y-10 text-base"></i>
                    ),
            };
        });

    useEffect(() => {
        const period = Number(selectedPeriod?.split(" ")[0]);
        const startDate = moment().startOf("month").format("MMM YYYY");
        const endDate = moment()
            .startOf("month")
            .add(period, "months")
            .subtract(1, "days")
            .format("MMM YYYY");
        const sameYear = startDate?.split(" ")[1] === endDate?.split(" ")[1];
        const sameMonth = startDate?.split(" ")[0] === endDate?.split(" ")[0];

        const text = sameMonth
            ? `(${endDate})`
            : sameYear
            ? "(" + startDate?.split(" ")[0] + "-" + endDate + ")"
            : "(" + startDate + "-" + endDate + ")";

        setDisplayPeriod(`${selectedPeriod} ${text}`);
        setDates({
            startDate: moment()?.format("YYYY-MM-DD"),
            endDate: moment(endDate)?.endOf("month")?.format("YYYY-MM-DD"),
        });
    }, [selectedPeriod]);

    const tableComponentHeader = [
        {
            title: "Forecasted qty to be used",
            widthClass:
                "!border !border-slate-200 w-[30%] max-sm:w-[200px] text-[12px] rounded-l-[4px] bg-slate-50",
            tooltipIcon: "forecasted-qty",
            tooltipContent:
                "The projected quantity of this component expected to be used across all SKUs it's part of—not just a single finished good based on future demand trends and planned production activities.",
        },
        {
            title: "Inventory forecast",
            widthClass: "!border !border-slate-200 w-[25%] max-sm:w-[200px] bg-slate-50",
            tooltipIcon: "inventory-forecast",
            tooltipContent:
                "A projection of future inventory requirements based on analysis of past sales data, demand trends, lead times, and safety stock to ensure optimal stock levels and avoid stockouts.",
        },
        {
            title: "Incoming Inventory",
            widthClass: "!border !border-slate-200 w-[25%] max-sm:w-[200px] bg-slate-50",
            tooltipIcon: "outgoing-pos",
            tooltipContent:
                "Purchase orders that have been issued to suppliers but not yet received, representing inventory expected to arrive and replenish stock.",
        },
        {
            title: "Month",
            widthClass:
                "!border !border-slate-200 w-[20%] max-sm:w-[200px] bg-slate-50 rounded-r-[4px]",
            tooltipIcon: "month",
            tooltipContent:
                "The specific calendar month used as a time frame for analyzing or forecasting inventory levels, sales, or purchase orders.",
        },
    ];

    const tableComponentBody = salesForecast?.map((comp, idx) => {
        return [
            {
                content: (
                    <div
                        className={`py-1.5 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                    >
                        <p className="text-[#042821]">
                            {millify(comp?.forecast?.forecast || 0, {
                                precision: 2,
                            })}
                            {` `}
                            {uom || ""}
                        </p>
                    </div>
                ),

                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200 !border",
            },

            {
                content: (
                    <div className={`py-1.5 pr-3 font-rocGroteskMedium text-sm `}>
                        <div className="flex items-center gap-1">
                            {comp?.forecast?.inventoryForecast <= 0 && (
                                <span className="material-symbols-outlined text-[14px] text-r-50">
                                    warning
                                </span>
                            )}
                            <p
                                className={` ${
                                    comp?.forecast?.inventoryForecast <= 0
                                        ? "text-[#FF3D1D]"
                                        : "text-[#042821]"
                                }`}
                            >
                                {millify(comp?.forecast?.inventoryForecast || 0, {
                                    precision: 2,
                                })}
                                {` `}
                                {uom || ""}
                            </p>
                        </div>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-1.5 pr-3 font-rocGroteskMedium border-slate-200 text-sm `}>
                        <p className="text-[#042821]">
                            {millify(comp?.forecast?.orderToArrive || 0, {
                                precision: 2,
                            })}
                            {` `}
                            {uom || ""}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-1.5 pr-3 font-rocGroteskMedium border-slate-200 text-sm `}>
                        <p className="text-[#042821]">
                            {moment(comp?.range?.start)?.format("MMMM") === moment()?.format("MMMM")
                                ? moment(comp?.range?.start)?.format("MMMM DD, YYYY")
                                : moment(comp?.range?.start)?.format("MMMM YYYY")}
                        </p>
                    </div>
                ),
                widthClass: "!border-slate-200 !border",
            },
        ];
    });

    let totalForecast = 0;
    let totalInventoryForecast = 0;
    let totalOrderToArrive = 0;

    if (salesForecast && salesForecast.length > 0) {
        salesForecast.forEach((comp) => {
            totalForecast += Number(comp?.forecast?.forecast) || 0;
            totalInventoryForecast += Number(comp?.forecast?.inventoryForecast) || 0;
            totalOrderToArrive += Number(comp?.forecast?.orderToArrive) || 0;
        });
    }

    const newRowData = [
        {
            content: (
                <div
                    className={`py-1.5 px-3 flex font-rocGroteskMedium items-center h-full text-sm `}
                >
                    <p className="text-[#042821] font-bold">
                        <p className="text-[#042821] font-bold">
                            {millify(totalForecast || 0, {
                                precision: 2,
                            })}
                            {` `}
                            {uom || ""}
                            {` `}
                            (next {selectedPeriod})
                        </p>
                    </p>
                </div>
            ),
            tableDataDivClass: "!pl-0",
            widthClass: "!border-slate-200 !border",
        },
        {
            // content: (
            //     <div className={`py-1.5 pr-3 font-rocGroteskMedium text-sm `}>
            //         <p className="text-[#042821] font-bold">
            //             {millify(totalInventoryForecast || 0, {
            //                 precision: 2,
            //             })}
            //             {` `}
            //             {uom || ""}
            //             {` `}
            //             (next {selectedPeriod})
            //         </p>
            //     </div>
            // ),
            // widthClass: "!border-slate-200",
            content: <></>,
            widthClass: "!border-none",
        },
        {
            // content: (
            //     <div className={`py-1.5 pr-3 font-rocGroteskMedium border-slate-200 text-sm `}>
            //         <p className="text-[#042821] font-bold">
            //             {millify(totalOrderToArrive || 0, {
            //                 precision: 2,
            //             })}
            //             {` `}
            //             {uom || ""}
            //             {` `}
            //             (next {selectedPeriod})
            //         </p>
            //     </div>
            // ),
            // widthClass: "!border-slate-200",
            content: <></>,
            widthClass: "!border-none",
        },
        {
            content: <></>,
            widthClass: "!border-none",
        },
    ];

    tableComponentBody.push(newRowData);

    useEffect(() => {
        setWarehouseId(warehouseDataValue?.value);
    }, [warehouseDataValue]);

    const multiBarChartOption = {
        // legend: { show: false },
        tooltip: {
            formatter: function (params) {
                return `
                <div>
                    <p style="margin-bottom: 10px">${params?.seriesName}</p>
                    <div style="display:flex ; align-items: center; gap: 10px;">
                        <div style="display:flex ; align-items: center; gap: 5px;">
                            <span style="width: 10px; height: 10px; background-color:${
                                params?.color
                            }; border-radius: 100%; "></span>
                            <span>${params?.value?.toLocaleString()} ${uom}</span>
                        </div> 
                    </div>
                </div>
                `;
            },
        },
        xAxis: {
            axisLabel: {
                formatter: "{value}",
                color: "#89939b",
            },
            type: "category",
            data: salesForecast?.map((item) => moment(item?.range?.start)?.format("MMM YYYY")),
            axisTick: {
                show: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: "#F1F5F9",
                },
            },
        },
        yAxis: {
            axisLabel: {
                formatter: "{value}",
                color: "#89939b",
                width: 200,
            },

            axisLine: {
                show: true,
                borderType: "dotted",
                lineStyle: {
                    color: "#F1F5F9",
                    type: "dashed",
                },
                nameTextStyle: {
                    color: "#89939b",
                },
            },
            axisTick: {
                show: true,
                alignWithLabel: false,
                lineStyle: {
                    type: "dashed",
                },
            },
            splitLine: {
                show: true,
                showMinLine: false,
                lineStyle: {
                    type: "dashed",
                },
            },
        },
        series: [
            {
                name: "Forecasted qty to be used",
                type: "bar",
                barWidth: "20px",
                data: salesForecast?.map((item) => {
                    const value = item?.forecast?.forecast;
                    return {
                        value,
                        itemStyle: {
                            borderRadius: value >= 0 ? [10, 10, 0, 0] : [0, 0, 10, 10],
                            color: "#56B648",
                        },
                    };
                }),
            },
            {
                name: "Inventory forecast",
                type: "bar",
                barWidth: "20px",
                data: salesForecast?.map((item) => {
                    const value = item?.forecast?.inventoryForecast;
                    return {
                        value,
                        itemStyle: {
                            borderRadius: value >= 0 ? [10, 10, 0, 0] : [0, 0, 10, 10],
                            color: value >= 0 ? "#6562E5" : "#FF3D1D",
                        },
                    };
                }),
            },
        ],
    };

    const hasNegativeForecast = salesForecast?.find(
        (item) => item?.forecast?.inventoryForecast < 0
    );
    const hasPositiveForecast = salesForecast?.find(
        (item) => item?.forecast?.inventoryForecast >= 0
    );
    const allZeroValues = salesForecast?.every((item) => item?.forecast?.inventoryForecast === 0);

    const lastSyncedForcast = lastSyncedTimeDiff(lastSyncedDate);
    return (
        <div className="py-5 relative bg-white rounded-[6px] shadow-faintShadow border border-[#E2E8F080] mb-8">
            <div className={`${type === "chat" ? "flex flex-col space-y-4" : "flex items-center justify-end"} pb-5 px-6 border-b border-slate-200 mb-6`}>
                <div className={`${type === "chat" ? "flex flex-col space-y-3" : "flex items-center space-x-4"}`}>
                    {fetchingSalesForecast ? (
                        <div>
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        </div>
                    ) : (
                        <div className="flex items-center whitespace-nowrap h-[40px] py-2 px-4 space-x-2">
                            <span className="w-[14px] h-[14px] rounded-[2px] bg-[#56B648]"></span>
                            <span className={`text-g-75 font-rocGroteskMedium text-sm`}>
                                Forecasted qty to be used
                            </span>
                        </div>
                    )}

                    {fetchingSalesForecast ? (
                        <div>
                            <Skeleton
                                height={10}
                                width={150}
                                highlightColor="#E2E8F0"
                                borderRadius={16}
                                baseColor="#F1F5F9"
                                className="custom-skeleton"
                            />
                        </div>
                    ) : (
                        <div className="flex items-center whitespace-nowrap h-[40px] py-2 px-4 space-x-2">
                            {(hasPositiveForecast || allZeroValues) && (
                                <span className="w-[14px] h-[14px] rounded-[2px] bg-[#6562E5]"></span>
                            )}

                            {hasNegativeForecast && (
                                <span className="w-[14px] h-[14px] rounded-[2px] bg-[#FF3D1D]"></span>
                            )}

                            <span className={`text-g-75 font-rocGroteskMedium text-sm`}>
                                Inventory forecast
                            </span>
                        </div>
                    )}
                    <div className="flex items-center rounded-[5px] border border-slate-200 ">
                        <div className="border-r border-slate-200">
                            {/* <Dropdown
                                handleChange={(name, value) => {
                                }}
                                dropdown={
                                    <div className="flex items-center  whitespace-nowrap h-[40px] py-2 px-4 space-x-1 ">
                                        <i className="ri-map-pin-line text-sm text-g-75 "></i>
                                        <span
                                            className={`text-g-75 font-rocGroteskMedium text-[13px]`}
                                        >
                                            {warehouseDataValue?.name || "Warehouses"}
                                        </span>
                                        {warehouseDataValue?.name && (
                                            <i
                                                onClick={() => {
                                                    clearFilter();
                                                }}
                                                className="ri-close-circle-line text-slate-500"
                                            ></i>
                                        )}
                                        <i className="ri-arrow-down-s-line text-g-75"></i>
                                    </div>
                                }
                                search={
                                    <div>
                                        <TextInput
                                            value={searchTwo}
                                            name={"search"}
                                            type={"text"}
                                            required={false}
                                            onChange={handleSearchTwo}
                                            onInput={handleDebouncedChangeTwo}
                                            inputPlaceholder="Search warehouse"
                                            containerClassname=""
                                            // inputContainerClassname={"!border-none !rounded-none "}
                                            // inputClassName="w-[50%]"
                                        />
                                    </div>
                                }
                                dropdownContainerClasses={`shadow-cardShadow-4  border border-slate-100  rounded `}
                                dropdownOptions={[
                                    {
                                        customChild: (
                                            <div>
                                                <div className="max-h-[280px]">
                                                    {searchResult?.map((warehouse) => {
                                                        return (
                                                            <div
                                                                key={warehouse?._id}
                                                                onClick={() => {
                                                                    filterChange(
                                                                        warehouse?.warehouseName,
                                                                        warehouse?._id
                                                                    );
                                                                }}
                                                            >
                                                                <div
                                                                    className={`flex items-center space-x-3 max-w-[300px] px-3 py-2 hover:bg-slate-100 cursor-pointer ${
                                                                        warehouseDataValue?.value ===
                                                                            warehouse?._id &&
                                                                        "bg-slate-100"
                                                                    }`}
                                                                >
                                                                    <i className="ri-map-pin-line text-base text-g-75 "></i>
                                                                    <div>
                                                                        <span
                                                                            className={`block text-g-75 text-sm font-rocGroteskMedium ${
                                                                                warehouse?.address
                                                                                    ?.address &&
                                                                                "mb-[2px]"
                                                                            }`}
                                                                        >
                                                                            {truncate(
                                                                                warehouse?.warehouseName,
                                                                                {
                                                                                    length: 40,
                                                                                }
                                                                            )}
                                                                        </span>
                                                                        <span className="block text-g-75 text-sm font-rocGroteskMedium ">
                                                                            {truncate(
                                                                                warehouse?.address
                                                                                    ?.address,
                                                                                {
                                                                                    length: 40,
                                                                                }
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ),
                                    },
                                ]}
                            /> */}

                            <MultiChannelSelect
                                value={salesForecastChannelFilterValue}
                                salesChannel={salesChannelOption?.map((item) => item.value)}
                                isLoading={salesChannelLoading}
                                onChange={(value) => {
                                    handleSalesForecastChannelChange(value);
                                }}
                                dropdown={
                                    <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 pl-3 pr-2 space-x-1 rounded-[5px]">
                                        <div className="flex items-center space-x-2">
                                            <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium capitalize">
                                                {truncate(salesForecastChannelFilterValue, {
                                                    length: 15,
                                                }) || "All channels"}
                                            </span>
                                            {!!salesForecastChannelFilterValue && (
                                                <i
                                                    onClick={(e) => {
                                                        e?.stopPropagation();
                                                        handleSalesForecastChannelChange("");
                                                    }}
                                                    className="ri-close-circle-fill text-sm text-slate-500"
                                                ></i>
                                            )}
                                        </div>
                                    </div>
                                }
                            />
                        </div>

                        <Dropdown
                            handleChange={(name, value) => {
                                setSelectedPeriod(value);
                            }}
                            dropdown={
                                <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1">
                                    <span className={`text-g-75 font-rocGroteskMedium text-[13px]`}>
                                        {displayPeriod}
                                    </span>
                                    <i className="ri-arrow-down-s-line text-g-75"></i>
                                </div>
                            }
                            dropdownContainerClasses={`shadow-cardShadow-4 !w-full !max-w-full min-w-full border border-slate-100 rounded !max-h-fit !pt-0`}
                            dropdownOptions={[
                                {
                                    label: "1 month",
                                    value: "1 month",
                                },
                                {
                                    label: "2 months",
                                    value: "2 months",
                                },
                                {
                                    label: "3 months",
                                    value: "3 months",
                                },
                                {
                                    label: "4 months",
                                    value: "4 months",
                                },
                                {
                                    label: "5 months",
                                    value: "5 months",
                                },
                                {
                                    label: "6 months",
                                    value: "6 months",
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>
            {salesForecast?.length === 0 && !fetchingSalesForecast ? (
                <div className="w-[100%] h-[400px] flex items-center justify-center">
                    <div className="flex items-center  gap-[3px] flex-col w-[353px] justify-center">
                        <img
                            src="https://storage.googleapis.com/heysynth_files/synth_webapp/icon_w8c4mn.png"
                            alt=""
                            className=""
                        />
                        <p className="text-g-75 font-rocGroteskBold text-base ">
                            {" "}
                            No forecast data available{" "}
                        </p>
                    </div>
                </div>
            ) : fetchingSalesForecast ? (
                <div className="flex justify-center">
                    <div className="mt-10 w-[80%]">
                        <div className="flex gap-5">
                            <div className="flex flex-col justify-between gap-[17px]">
                                <span className="block text-[9px] text-slate-500">70k</span>
                                <span className="block text-[9px] text-slate-500">60k</span>
                                <span className="block text-[9px] text-slate-500">50k</span>
                                <span className="block text-[9px] text-slate-500">40k</span>
                                <span className="block text-[9px] text-slate-500">30k</span>
                                <span className="block text-[9px] text-slate-500">20k</span>
                                <span className="block text-[9px] text-slate-500">10k</span>
                            </div>

                            <div className="flex gap-2 justify-between w-full">
                                {Array.from({ length: 6 }).map((_, idx) => (
                                    <div key={idx} className="flex gap-1 items-end">
                                        <Skeleton
                                            height={60}
                                            width={10}
                                            highlightColor="#E2E8F0"
                                            borderRadius={1}
                                            baseColor="#F1F5F9"
                                            className="custom-skeleton"
                                        />
                                        <Skeleton
                                            height={90}
                                            width={10}
                                            highlightColor="#E2E8F0"
                                            borderRadius={1}
                                            baseColor="#F1F5F9"
                                            className="custom-skeleton"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 justify-between pt-[15px] border-t border-slate-200 ml-10">
                            <span className="block text-[9px] text-slate-500">Jan</span>
                            <span className="block text-[9px] text-slate-500">Feb</span>
                            <span className="block text-[9px] text-slate-500">Mar</span>
                            <span className="block text-[9px] text-slate-500">Apr</span>
                            <span className="block text-[9px] text-slate-500">May</span>
                            <span className="block text-[9px] text-slate-500">Jun</span>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={`px-6 ${type === "chat" ? "block" : "xl:flex"} items-center`}>
                    <div className={`${type === "chat" ? "w-full mb-6" : "xl:w-[55%]"}`}>
                        <ReactECharts option={multiBarChartOption} />
                    </div>
                    <div className={`relative ${type === "chat" ? "w-full" : "xl:w-[45%] xl:left-[-20px]"}`}>
                        <div className="shadow-sm">
                            <CustomTable
                                tableBody={tableComponentBody}
                                tableHeader={tableComponentHeader}
                                isAllSelectable={false}
                                isCellSelectable={false}
                                isCellBordered={true}
                                stickyHeader={true}
                                scrollWithin={false}
                                headerItemClass="font-rocGroteskMedium !border-slate-200  !text-slate-700 !py-[6px] !text-xs"
                                isScrollable={false}
                                bodyItemClass="!border-slate-200 hover:!bg-transparent !cursor-default"
                                headerContainerClass="!bg-slate-50 !border-slate-200"
                                tableClass="!border-none !border-[0px]"
                            />
                        </div>
                    </div>
                </div>
            )}
            {fetchingSalesForecast && (
                <div className="w-full h-full  absolute top-0 left-0">
                    <div className="w-full h-full flex justify-center items-center">
                        {/* <Loader size={10} /> */}
                        <p className="text-slate-600 text-base font-rocGroteskMedium">
                            Generating forecast...
                        </p>
                    </div>
                </div>
            )}
            {lastSyncedDate && (
                <div
                    className={`ml-4 flex !w-fit items-center space-x-1 border  rounded-[3px] px-2  text-sm font-rocGroteskMedium ${
                        lastSyncedForcast === 0
                            ? "bg-[#DCFCE7] border-[#16A34A] text-[#16A34A]"
                            : lastSyncedForcast === 1
                            ? "bg-[#FEF3C7] border-[#D97706] text-[#D97706]"
                            : lastSyncedForcast === 2
                            ? "bg-[#FFEDD5] border-[#EA580C] text-[#EA580C]"
                            : "bg-[#FEE2E2] border-[#DC2626] text-[#DC2626]"
                    }`}
                >
                    <i
                        className={`ri-refresh-line text-sm  ${
                            lastSyncedForcast === 0
                                ? "text-[#16A34A]"
                                : lastSyncedForcast === 1
                                ? " text-[#D97706]"
                                : lastSyncedForcast === 2
                                ? " text-[#EA580C]"
                                : "text-[#DC2626]"
                        }`}
                    ></i>{" "}
                    <p className="text-sm">Last Synced: {moment(lastSyncedDate)?.fromNow()}</p>
                    <SynthTooltip
                        content={
                            "The most recent date this data was synced from your source systems."
                        }
                        divId={"last-synced-forecast"}
                        icon={
                            <i
                                className={`ri-information-line ${
                                    lastSyncedForcast === 0
                                        ? "text-[#16A34A]"
                                        : lastSyncedForcast === 1
                                        ? " text-[#D97706]"
                                        : lastSyncedForcast === 2
                                        ? " text-[#EA580C]"
                                        : "text-[#DC2626]"
                                } text-sm`}
                            ></i>
                        }
                        styling={{ width: "250px" }}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesComponent;
